import { useEffect, useState, useRef } from "react";
import { fetchChart, formatUSD } from "@/lib/api";
import { getCandleCrosshairPoint } from "@/lib/crosshair";
import { useI18n } from "@/i18n";
import { ResponsiveContainer, ComposedChart, Area, Line, XAxis, YAxis, Tooltip, CartesianGrid, Bar, Cell } from "recharts";
import { Button } from "@/components/ui/button";
import { LineChart, CandlestickChart } from "lucide-react";

const TIMEFRAMES = [
  { key: "hour1", label: "1H" },
  { key: "hour4", label: "4H" },
  { key: "1", label: "1D" },
  { key: "7", label: "1W" },
  { key: "30", label: "1M" },
];

const ACCENTS = { BTC: "#EAB308", ETH: "#60A5FA", SOL: "#9945FF", HYPE: "#22D3EE" };
export const CANDLE_CROSSHAIR_DASH = "4 4";
const CHART_HEIGHT = 380;
const CHART_MARGIN = { top: 10, right: 8, left: 0, bottom: 0 };
const PRICE_AXIS_WIDTH = 70;

// Custom candle bar shape
const Candle = (props) => {
  const { x, y, width, height, payload } = props;
  if (!payload) return null;
  const { o, c, h, l } = payload;
  const up = c >= o;
  const color = up ? "#22C55E" : "#EF4444";
  // recharts feeds y and height for the bar which corresponds to |c-o|.
  // We need scale info: use payload._range from parent for accurate mapping.
  const range = payload._range;
  if (!range) return null;
  const { min, max, top, bottom } = range;
  const scale = (v) => bottom - ((v - min) / (max - min)) * (bottom - top);
  const cx = x + width / 2;
  const wickTop = scale(h);
  const wickBot = scale(l);
  const bodyTop = scale(Math.max(o, c));
  const bodyBot = scale(Math.min(o, c));
  const bodyH = Math.max(1, bodyBot - bodyTop);
  const w = Math.max(2, width * 0.7);
  return (
    <g>
      <line x1={cx} x2={cx} y1={wickTop} y2={wickBot} stroke={color} strokeWidth={1} />
      <rect x={cx - w / 2} y={bodyTop} width={w} height={bodyH} fill={color} stroke={color} />
    </g>
  );
};

export default function PriceChart({ symbol, price }) {
  const { t } = useI18n();
  const [days, setDays] = useState("1");
  const [chartType, setChartType] = useState("line"); // "line" | "candle"
  const [points, setPoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [crosshair, setCrosshair] = useState(null);
  const [chartWidth, setChartWidth] = useState(0);
  const color = ACCENTS[symbol] || "#22C55E";
  const wsTimer = useRef(null);
  const chartFrameRef = useRef(null);

  useEffect(() => {
    const frame = chartFrameRef.current;
    if (!frame) return undefined;
    const updateSize = () => setChartWidth(frame.clientWidth);
    updateSize();
    const observer = new ResizeObserver(updateSize);
    observer.observe(frame);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    setCrosshair(null);
  }, [symbol, days, chartType]);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    fetchChart(symbol, days)
      .then((d) => { if (mounted) setPoints(d.points || []); })
      .catch(() => {})
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, [symbol, days]);

  // Live update: refresh chart every 15s (last candle updates in real time)
  useEffect(() => {
    if (wsTimer.current) clearInterval(wsTimer.current);
    wsTimer.current = setInterval(() => {
      fetchChart(symbol, days).then((d) => setPoints(d.points || [])).catch(() => {});
    }, 15000);
    return () => clearInterval(wsTimer.current);
  }, [symbol, days]);

  // Live tick: update the last close with the current live price so it feels alive
  useEffect(() => {
    if (!price?.price || !points.length) return;
    setPoints((prev) => {
      if (!prev.length) return prev;
      const last = prev[prev.length - 1];
      const nl = { ...last, c: price.price, p: price.price, h: Math.max(last.h ?? last.p, price.price), l: Math.min(last.l ?? last.p, price.price) };
      return [...prev.slice(0, -1), nl];
    });
    // eslint-disable-next-line
  }, [price?.price]);

  // Compute Y range for candlestick scaling
  const highs = points.map((p) => p.h ?? p.p);
  const lows = points.map((p) => p.l ?? p.p);
  const yMin = Math.min(...lows);
  const yMax = Math.max(...highs);
  const yPad = (yMax - yMin) * 0.05 || 1;
  const domain = [yMin - yPad, yMax + yPad];

  // Attach _range info per point so candle shape can render
  const chartData = points.map((p) => ({ ...p, _range: { min: domain[0], max: domain[1], top: 10, bottom: 380 } }));

  const first = points[0]?.p || 0;
  const last = points[points.length - 1]?.c || points[points.length - 1]?.p || 0;
  const change = first ? ((last - first) / first) * 100 : 0;
  const up = change >= 0;

  return (
    <div className="border border-border/60 rounded-sm bg-[#09090B] p-3 sm:p-5" data-testid="price-chart-panel">
      <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 text-[11px] font-mono tracking-widest text-zinc-500 uppercase">
            <span>{symbol} / USD</span>
            <span className="pulse-dot" />
          </div>
          <div className="flex items-baseline gap-3 mt-1">
            <div className="font-mono text-3xl sm:text-4xl font-bold" data-testid="chart-price">
              {price ? formatUSD(price.price) : <span className="shimmer inline-block w-40 h-8 rounded" />}
            </div>
            <div className={`font-mono text-sm ${up ? "text-emerald-400" : "text-red-400"}`}>
              {up ? "+" : ""}{change.toFixed(2)}%
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Chart type toggle */}
          <div className="flex gap-0 border border-border/60 rounded-sm" data-testid="chart-type-selector">
            <button
              data-testid="chart-type-line"
              onClick={() => setChartType("line")}
              className={`h-7 px-2 flex items-center gap-1 text-[10px] font-mono uppercase ${
                chartType === "line" ? "bg-emerald-500/20 text-emerald-300" : "text-zinc-500 hover:text-white"
              }`}
            >
              <LineChart className="w-3 h-3" strokeWidth={1.5} /> {t("chart_line")}
            </button>
            <button
              data-testid="chart-type-candle"
              onClick={() => setChartType("candle")}
              className={`h-7 px-2 flex items-center gap-1 text-[10px] font-mono uppercase ${
                chartType === "candle" ? "bg-emerald-500/20 text-emerald-300" : "text-zinc-500 hover:text-white"
              }`}
            >
              <CandlestickChart className="w-3 h-3" strokeWidth={1.5} /> {t("chart_candle")}
            </button>
          </div>
          {/* Timeframes */}
          <div className="flex gap-1" data-testid="timeframe-selector">
            {TIMEFRAMES.map((tf) => (
              <Button
                key={tf.key}
                data-testid={`timeframe-${tf.label}`}
                onClick={() => setDays(tf.key)}
                variant="ghost"
                size="sm"
                className={`h-7 px-2.5 text-[11px] font-mono rounded-sm ${
                  days === tf.key ? "bg-white/10 text-white" : "text-zinc-500 hover:text-white hover:bg-white/5"
                }`}
              >
                {tf.label}
              </Button>
            ))}
          </div>
        </div>
      </div>

      <div
        ref={chartFrameRef}
        className="h-[380px] relative"
        onMouseLeave={() => chartType === "candle" && setCrosshair(null)}
      >
        {loading ? (
          <div className="w-full h-full shimmer rounded-sm" />
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={chartData}
              margin={CHART_MARGIN}
              onMouseMove={(event) => {
                if (chartType !== "candle") return;
                const plotRight = Math.max(CHART_MARGIN.left, chartWidth - CHART_MARGIN.right - PRICE_AXIS_WIDTH);
                setCrosshair(getCandleCrosshairPoint(event, {
                  left: CHART_MARGIN.left,
                  right: plotRight,
                  top: CHART_MARGIN.top,
                  bottom: CHART_HEIGHT - CHART_MARGIN.bottom,
                }));
              }}
              onMouseLeave={() => chartType === "candle" && setCrosshair(null)}
            >
              <defs>
                <linearGradient id={`chart-${symbol}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={color} stopOpacity={0.35} />
                  <stop offset="100%" stopColor={color} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#27272A" strokeOpacity={0.4} vertical={false} />
              <XAxis
                dataKey="t"
                tickFormatter={(v) => {
                  const d = new Date(v);
                  const intraday = days === "1" || days === "hour1" || days === "hour4";
                  if (intraday) return d.getHours().toString().padStart(2, "0") + ":" + d.getMinutes().toString().padStart(2, "0");
                  return d.getMonth() + 1 + "/" + d.getDate();
                }}
                stroke="#52525B"
                fontSize={10}
                tickLine={false}
                axisLine={false}
                minTickGap={40}
              />
              <YAxis
                domain={domain}
                stroke="#52525B"
                fontSize={10}
                tickLine={false}
                axisLine={false}
                width={70}
                tickFormatter={(v) => (v >= 1000 ? "$" + (v / 1000).toFixed(1) + "k" : "$" + v.toFixed(2))}
                orientation="right"
              />
              <Tooltip
                allowEscapeViewBox={{ x: true, y: true }}
                cursor={chartType === "candle"
                  ? { stroke: "#52525B", strokeOpacity: 0.8, strokeWidth: 1, strokeDasharray: CANDLE_CROSSHAIR_DASH }
                  : { stroke: color, strokeWidth: 1, strokeDasharray: "4 4" }}
                wrapperStyle={{ pointerEvents: "none", zIndex: 20 }}
                contentStyle={{ background: "#09090B", border: `1px solid ${color}`, borderRadius: 2, color: "#FFFFFF", fontFamily: "JetBrains Mono", fontSize: 12, maxWidth: "min(32rem, calc(100vw - 2rem))", whiteSpace: "normal", zIndex: 20 }}
                labelStyle={{ color, marginBottom: 4 }}
                itemStyle={{ color: "#FFFFFF", padding: 0 }}
                labelFormatter={(v) => new Date(v).toLocaleString()}
                formatter={(v, name, p) => {
                  if (chartType === "candle") {
                    const d = p?.payload || {};
                    return [`O ${formatUSD(d.o)} · H ${formatUSD(d.h)} · L ${formatUSD(d.l)} · C ${formatUSD(d.c)}`, symbol];
                  }
                  return [formatUSD(v), symbol];
                }}
              />
              {chartType === "line" ? (
                <Area type="monotone" dataKey="c" stroke={color} strokeWidth={1.8} fill={`url(#chart-${symbol})`} />
              ) : (
                <Bar dataKey="c" shape={<Candle />} isAnimationActive={false} />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        )}
        {chartType === "candle" && crosshair && chartWidth > 0 && (
          <svg
            className="absolute inset-0 z-10 pointer-events-none"
            width="100%"
            height={CHART_HEIGHT}
            viewBox={`0 0 ${chartWidth} ${CHART_HEIGHT}`}
            aria-hidden="true"
          >
            <line x1={CHART_MARGIN.left} x2={Math.max(CHART_MARGIN.left, chartWidth - CHART_MARGIN.right - PRICE_AXIS_WIDTH)} y1={crosshair.y} y2={crosshair.y} stroke="#52525B" strokeOpacity="0.8" strokeDasharray={CANDLE_CROSSHAIR_DASH} />
          </svg>
        )}
      </div>

      {price && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mt-4 pt-4 border-t border-border/60 text-xs font-mono">
          <div>
            <div className="text-zinc-500 uppercase text-[10px]">{t("high_24h")}</div>
            <div className="text-white">{formatUSD(price.high_24h)}</div>
          </div>
          <div>
            <div className="text-zinc-500 uppercase text-[10px]">{t("low_24h")}</div>
            <div className="text-white">{formatUSD(price.low_24h)}</div>
          </div>
          <div>
            <div className="text-zinc-500 uppercase text-[10px]">{t("change_7d")}</div>
            <div className={price.change_7d >= 0 ? "text-emerald-400" : "text-red-400"}>
              {price.change_7d != null ? (price.change_7d >= 0 ? "+" : "") + price.change_7d.toFixed(2) + "%" : "-"}
            </div>
          </div>
          <div>
            <div className="text-zinc-500 uppercase text-[10px]">{t("change_24h")}</div>
            <div className={price.change_24h >= 0 ? "text-emerald-400" : "text-red-400"}>
              {(price.change_24h >= 0 ? "+" : "") + price.change_24h.toFixed(2)}%
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
